import React, { ComponentProps, useMemo } from 'react'
import { Col, Container, Row, Hidden } from 'ui/elements/Grid'
import Button from 'ui/elements/Button'
import { useTranslation } from 'gatsby-plugin-react-i18next'

import Contact from 'components/shared/Contact'
import Seo from 'components/shared/Seo'
import { palette as openProgramsPalette } from 'modules/OpenPrograms/utils'
import { palette as customizedProgramsPalette } from 'modules/CustomizedPrograms/utils'
import { palette as conferencesPalette } from 'modules/Conferences/utils'

import { HeroPolygon, HeroCorner, Hero } from '../Hero'
import * as S from './styles'

import { graphql, HeadFC } from 'gatsby'
import Link from 'widgets/Link'
import { DefaultHeadFC, useGetInternalLink } from 'utils/helpers'
import OpticalHeading from 'ui/elements/Heading/OpticalHeading'

const CtaAction: React.FC<{
  cta: Queries.CtaDataFragment
}> = ({ cta }) => {
  const [t] = useTranslation('common')

  const internalLink = useGetInternalLink(
    cta.internalLink ?? undefined,
    undefined,
    undefined,
    cta.eventCategory
  )

  if (!cta.internalLink && !cta.externalLink?.localized) {
    return null
  }

  if (cta.externalLink?.localized) {
    return (
      <Button
        as="a"
        href={cta.externalLink.localized}
        variant="contained"
        color="shade"
        iconName="arrow-right"
        target="_blank"
      >
        {cta.text?.localized ?? t('findOutMore')}
      </Button>
    )
  }

  return (
    <Button
      as={Link}
      to={internalLink}
      variant="contained"
      color="shade"
      iconName="arrow-right"
    >
      {t('findOutMore')}
    </Button>
  )
}

export const PageLayoutSeo: React.FC<
  {
    headProps: ComponentProps<
      DefaultHeadFC<{
        title?: Queries.Maybe<{ localized?: Queries.Maybe<string> }>
        meta?: Queries.Maybe<Queries.MetaFragmentFragment>
        palette?: string | null
        noindex?: Queries.Maybe<boolean>
      }>
    >
  } & Omit<
    ComponentProps<typeof Seo>,
    'title' | 'description' | 'noindex' | 'language'
  >
> = ({ headProps: { data, pageContext }, ...seoProps }) => {
  const { i18n } = pageContext
  const pageTitle = useMemo(() => {
    return (
      data?.meta?.title?.[i18n.language as 'de' | 'en'] ??
      data?.title?.localized
    )
  }, [data, i18n])

  const metaDescription = useMemo(() => {
    const description = data?.meta?.description

    return description?.[i18n.language as 'de' | 'en'] ?? description?.en
  }, [data, i18n])

  return (
    <Seo
      title={pageTitle ?? undefined}
      description={metaDescription ?? undefined}
      noindex={data?.noindex ?? false}
      pageContext={pageContext}
      meta={data?.meta}
    />
  )
}

const PageLayout: React.FC<
  React.PropsWithChildren<{
    sidebar?: React.FC<Queries.BasePageFragment>
    heroProps?: { type: 'polygon' | 'corner' } & (
      | Partial<ComponentProps<typeof HeroPolygon>>
      | Partial<ComponentProps<typeof HeroCorner>>
    )
    data?: Omit<Queries.BasePageFragment, 'hero'> & {
      palette?: string | null
      noindex?: Queries.Maybe<boolean>
      hero: Hero
    }
    title?: string
    cta?: Queries.Maybe<Queries.CtaDataFragment>
    // required here as the magic layout cannot require props but the page still has to be forced to load it
    infoBarConfig: Queries.InfoBarConfigFragment | undefined | null
    headerCTASiteConfig:
      | Queries.HeaderCTAConfigFragment['headerCTASiteConfig']
      | undefined
      | null
    structuredData?: any[]
    separateSubtitle?: boolean
  }>
> = ({
  children,
  sidebar: Sidebar,
  heroProps: _heroProps,
  cta,
  structuredData,
  separateSubtitle,
  ...layoutProps
}) => {
  const contact = layoutProps.data?.contact
  const hasSidebar = !!Sidebar && typeof Sidebar === 'function'

  const pagePalette = useMemo(() => {
    const palette = layoutProps.data?.palette
    switch (palette) {
      case 'open-programs':
        return openProgramsPalette
      case 'customized-programs':
        return customizedProgramsPalette
      case 'conferences':
        return conferencesPalette
      default:
        return undefined
    }
  }, [layoutProps])

  const subtitle = useMemo(() => {
    return (
      _heroProps?.subheading ?? layoutProps?.data?.hero?.subtitle?.localized
    )
  }, [layoutProps, _heroProps])

  const heroProps = useMemo(() => {
    let hero
    if (layoutProps.data?.hero?.__typename === 'SanityHero') {
      hero = layoutProps.data.hero
    }

    if (_heroProps?.hero) {
      hero = _heroProps?.hero
    }

    if (layoutProps.data?.hero?.__typename === 'SanityExtendedImageLocalized') {
      hero = {
        ..._heroProps,
        image: layoutProps.data.hero,
      }
    }

    if (!hero) {
      return
    }

    return {
      hero,
      ..._heroProps,
      subheading: separateSubtitle ? null : subtitle,
    }
  }, [layoutProps, _heroProps, subtitle, separateSubtitle])

  const HeroComponent = useMemo(() => {
    if (_heroProps?.type === 'polygon') return HeroPolygon
    if (_heroProps?.type === 'corner') return HeroCorner
  }, [_heroProps?.type])

  const headerColor = useMemo(() => {
    return _heroProps?.type === 'polygon'
      ? pagePalette?.variant ?? 'primary'
      : undefined
  }, [_heroProps, pagePalette])

  return (
    <S.PageLayout>
      {!!HeroComponent && heroProps && (
        <HeroComponent
          color={headerColor as any}
          action={cta ? <CtaAction cta={cta} /> : undefined}
          {...heroProps}
        />
      )}

      {/* @ts-ignore */}
      <Container as={S.MainContainer} fluid={true} md={true} sm={true}>
        <Row>
          {Sidebar && hasSidebar && (
            <Col $xs={12} $md={4} $xl={3} as="aside">
              <Sidebar {...layoutProps.data} />
            </Col>
          )}
          <Col
            $xs={12}
            $md={hasSidebar ? 8 : 12}
            $xl={hasSidebar ? 9 : 12}
            as={S.MainContainer}
          >
            {/* @ts-ignore */}
            {separateSubtitle && subtitle && (
              <Hidden $xs $sm>
                <OpticalHeading level={1} renderAs="h2" color={headerColor}>
                  {subtitle}
                </OpticalHeading>
              </Hidden>
            )}
            {children}
          </Col>
        </Row>
      </Container>
      {contact && (
        <Contact
          //@ts-ignore
          contact={contact}
        />
      )}
    </S.PageLayout>
  )
}

export default PageLayout

export const query = graphql`
  fragment FacultyBasePage on SanityFacultyIndex {
    hero {
      ...HeroFragment
    }
    title {
      localized
    }
    meta {
      ...MetaFragment
    }
  }
  fragment BasePage on SanityLandingPageOrPage {
    ... on SanityPage {
      hero {
        ...HeroFragment
      }
      title {
        localized
      }
      meta {
        ...MetaFragment
      }
      contact {
        ...ContactFragment
      }
      palette
    }

    ... on SanityLandingPage {
      hero {
        ...HeroFragment
      }
      title {
        localized
      }
      meta {
        ...MetaFragment
      }
      contact {
        ...ContactFragment
      }
    }
  }

  fragment CtaData on SanityLocalizedCta {
    text {
      localized(locale: $language)
    }
    externalLink {
      localized(locale: $language)
    }
    internalLink: _rawInternalLink(resolveReferences: { maxDepth: 2 })
    eventCategory
  }
`
